import { UserResult } from "services/accountService/models/AccountType";
import { FinancingTypesResult } from "services/orders/models/OrdersTypes";
import { FileResult, Locale } from "services/shared-models/sharedTypes";
import { FileResponse } from "services/shared-models/sharedTypes";

// Statistics Types
export interface ProgramStatisticsResult {
  programTypeId: number;
  programTypeName: string;
  totalPrograms: number;
}

export interface ProgramStatisticsCountResult {
  totalPrograms: number;
  totalActiveProgram: number;
  totalInActiveProgram: number;
}

export type ProgramDetailsResult = {
  displayName: string;
  description: string;
  ruleAndConditionName?: string;
  ruleAndConditionTemplate?: string;
  commitmentName?: string;
  commitmentTemplate?: string;
  fileAttachmentLabel?:string;
  centreManagementLabel?:string;
  researchFieldLabel?:string;
  fromSubjectLabel?:string;
  locale?: Locale;
};

export type AudienceTypesResult = {
  id: number;
  audienceTypeDetail: {
    displayName: string;
    locale: string;
  };
};

export interface ProgramLocalResult {
  id: number;
  adsImage: string;
  programDetail: ProgramDetailsResult;
  programType: string;
  adsEndDate: Date;
  programOrders: number;
  programClassification: string;
  fundingDurationInMonths: number;
  status: string;
  audienceTypes: AudienceTypesResult[];
  programCycle?: ProgramCycleLocalResult;
}

export interface ProgramCycleLocalResult {
  id: number;
  programCycleDetails: LookupDetailsResult[];
  programCycleYear: ProgramCycleYearResult;
}
export interface ProgramCycleYearResult {
  id: number;
  programCycleYearDetail: LookupDetailsResult;
}
export interface LookupDetailsResult {
  id: number;
  displayName: string;
  locale: string;
}

export interface ProgramLocalItemResult {
  id: number;
  status: string;
  serialNumber: string;
  programDetails: ProgramDetailsResult[];
  classificationProgramId: number;
  classificationProgramName: string;
  adsImage: string;
  programTypeId: number;
  programTypeName:string;
  contractStartDate: Date;
  fundingDurationInMonths: number;
  adsStartDate: Date;
  adsEndDate: Date;
  fundingAmount: number;
  audienceTypes: AudienceTypesResult[];
  programCycleId?: number;
  programCycleYearId?: number;
  adsImageId?: string;
  contractId?: number;
  formBuilderSchema?: string;
  files: FileResult[];
  financingCalculationItems?: {
    id: number;
    financingType: FinancingTypesResult;
  }[];

  programMinutesResult?: ProgramMinutes;
  rejectionReasons?: RejectionReasonResult[];
}

export type ProgramMinutes = {
  id: number;
  committeeDecisionNumber: string;
  committeeDecisionDate: string;
  minuteDescription: string;
  files: FileResult[];
};

export interface ConditionsAndRulesResultResponse {
  id: number;
  conditionAndRuleDetail: {
    displayName: string;
    description: string;
    templateContent: string;
    locale: Locale;
  };
  isActive: boolean;
  createdOn: Date;
  updatedBy: string;
  updatedOn: Date;
  order: number;
}

export interface PDFTemplatesResultResponse {
  id: number;
  pdfTemplateDetail: {
    displayName: string;
    description: string;
    templateContent: string;
    locale: Locale;
  };
  isActive: boolean;
  createdOn: Date;
  updatedBy: string;
  updatedOn: Date;
  order: number;
}

export interface ConditionsAndRulesByIDResponse {
  id: number;
  isActive: boolean;
  conditionAndRuleDetails: {
    displayName: string;
    description: string;
    templateContent: string;
    locale: Locale;
  }[];
}

export interface PDFTemplateByIDResponse {
  id: number;
  isActive: boolean;
  templateTypeId: number;
  pdfTemplateDetails: {
    displayName: string;
    description: string;
    templateContent: string;
    locale: Locale;
  }[];
}

export enum FinancingTypes {
  PrimaryParticipant = 7,
  SecondaryParticipant = 9,
  Other = 3,
}

export interface ProgramByIDResponse {
  id: number;
  status: string;
  serialNumber: string;
  programManagerId?: string;
  numberOfActiveOrder: number;
  isFileAttachmentHidden?: boolean;
  isFileAttachmentRequired?: boolean;
  isCentreManagementHidden?: boolean;
  isCentreManagementRequired?: boolean;
  isResearchFieldHidden?: boolean;
  isResearchFieldRequired?: boolean;
  isFromSubjectHidden?: boolean;
  isFromSubjectRequired?: boolean;
  programDetails: {
    displayName: string;
    description: string;
    ruleAndConditionName: string;
    ruleAndConditionTemplate: string;
    commitmentName: string;
    commitmentTemplate: string;
    fileAttachmentLabel?:string;
    centreManagementLabel?:string;
    researchFieldLabel?:string;
    fromSubjectLabel?:string;
    locale: Locale;
  }[];
  classificationProgramId: number;
  programTypeId: number;
  contractStartDate: Date;
  fundingDurationInMonths: number;
  adsStartDate: Date;
  adsEndDate: Date;
  fundingAmount: number;
  audienceTypes: {
    id: number;
    audienceTypeDetail: {
      displayName: string;
      locale: Locale;
    };
  }[];
  programCycleYearId: number;
  programCycleId: number;
  adsImageId: string;
  adsImage: string;
  adsImageSize: string;
  adsImageName: string;
  contractId: number;
  formBuilderSchema: string;
  financingCalculationItems: {
    id: number;
    fromValue: number;
    toValue: number;
    notes?: string;
    financingType: {
      id: number;
      repetitionAllowed?: number;
      financingTypesDetail: {
        displayName: string;
        locale: Locale;
      };
      calculationWay: {
        id: number;
        calculationWayDetail: {
          displayName: string;
          locale: Locale;
        };
      };
    };
  }[];
  files?: FileResponse[];
  programTypeName: string;
  classificationProgramName: string;
  programCycleDetail: {
    displayName: string;
    locale: Locale;
  };
  programCycleYearDetail: {
    displayName: string;
    locale: Locale;
  };
  numberOfOrders?: number;
  programMinutes?: ProgramMinutes;
  workflowGid: string;
  rejectionReasons?: RejectionReasonResult[];
}

export type CreateProgramMinutes = {
  committeeDecisionNumber: string;
  committeeDecisionDate: string;
  minuteDescription: string;
  filesIds: string[];
  actionName?: string | null;
  serialGuid?: string | null;
  notes?: string;
};

export type ProgramActionResult = {
  action: string;
  processInstanceId: number;
  folio?: string;
  workflowName?: string;
  appliedBy: UserResult;
  createdOn?: string;
  status?: string;
  notes?: string;
};

export const CalculationWayLookup = {
  Monthly: {
    id: 1,
  },
  Fixed: {
    id: 2,
  },
  PagesCount: {
    id: 6,
  },
};

export type WorkflowDesignProcessLocalResult = {
  workflowGid: string;
  workflowName: string;
  workflowDesignProcessDetail: WorkflowServerProcessLocalResult;
  usageCount: number;
  versionsCount: number;
};

export type WorkflowServerProcessLocalResult = {
  workflowGid: string;
  workflowName: string;
  version: number;
  isDefault: boolean;
  deployOn: string;
};

export type RejectionReasonResult = {
  id: number;
  programId: number;
  displayName: string;
  locale: string;
};

export type RejectionReasonRequest = {
  id?: number;
  displayName: string;
  locale: string;
};

// Dashboard Types
export interface DashboardFilterRequest {
  year?: number;
  collegeIds?: number[];
}

export interface DashboardSummaryResult {
  receivedApplications: number;
  acceptedApplications: number;
  acceptanceRate: number;
  totalFundingInMillions: number;
}

export interface MonthlyApplicationsChartResult {
  month: string;
  monthNumber: number;
  count: number;
}

export interface ApplicationsByCollegeChartResult {
  collegeId: number;
  collegeName: string;
  count: number;
}

export interface ApplicationsByRankAndGenderChartResult {
  rank: string;
  rankOrder: number;
  maleCount: number;
  femaleCount: number;
  totalCount: number;
}

export interface ApplicationsByGenderChartResult {
  [key: string]: any;
  gender: string;
  count: number;
  percentage: number;
}

export interface ApplicationsByDepartmentChartResult {
  [key: string]: any;
  departmentId: number;
  departmentName: string;
  count: number;
}

// Research Groups Dashboard Types
export interface ResearchGroupsDashboardFilterRequest {
  year?: number;
  collegeIds?: number[];
}

export interface ResearchGroupsSummaryResult {
  totalApplicationsReceived: number;
  acceptedApplications: number;
  acceptanceRate: number;
  totalFundingInMillions: number;
}

export interface ApplicationsByFacultyChartResult {
  facultyName: string;
  rejectedCount: number;
  acceptedCount: number;
  totalCount: number;
}

export interface ApplicationsByLocalClassificationChartResult {
  quarter: string;
  classificationType: string;
  count: number;
  percentage: number;
}

export interface RejectionReasonsChartResult {
  rejectionReason: string;
  count: number;
}

export interface ApplicationDetailsByStatementResult {
  statementNumber: string;
  certificateCount: number;
  supportAmountInThousands: number;
}
